/**
 * The AutoGrab sourcing feed — what Direct Auto considers a sourceable listing.
 *
 * The rules here come from the saved view the team actually works from:
 *
 *   app.autograb.com.au/sourcing
 *     ?sort_by=listed_at&sort_descending=true
 *     &product=sourcing
 *     &excluded_tag_ids=new,new_to_order,damaged,writeoff,trash,mismatch,auction,
 *                       damaged/major_damage,damaged/misc_damage,
 *                       damaged/not_running,damaged/hail,missing_odometer
 *     &listing_categories=retail
 *
 * Two things worth knowing before reading further.
 *
 * First, AutoGrab's public API does not expose that feed. The documented
 * sourcing endpoints are `/sourcing/market_overlay/{vehicle_id}` and market
 * statistics — both keyed to a single vehicle — plus `POST /stock`, which
 * pushes our stock *to* them. There is no documented "give me the listing feed
 * with these filters" call. Pulling it needs that endpoint enabled on the Direct
 * Auto account; until then `feedEndpointConfigured()` is false and the app says
 * so instead of pretending.
 *
 * Second, the filters are worth having regardless of where the listings come
 * from. They are a description of what Direct Auto will and will not buy, so
 * they are applied to anything we do get — including the market-overlay
 * listings we already pull — rather than living only inside a URL someone
 * bookmarked.
 */

/** Tags that disqualify a listing. Straight from the saved view. */
export const EXCLUDED_TAG_IDS = [
  'new',
  'new_to_order',
  'damaged',
  'writeoff',
  'trash',
  'mismatch',
  'auction',
  'damaged/major_damage',
  'damaged/misc_damage',
  'damaged/not_running',
  'damaged/hail',
  'missing_odometer',
] as const

/** Only retail listings. Trade and auction stock is out of scope. */
export const LISTING_CATEGORIES = ['retail'] as const

export const SOURCING_SORT = { sortBy: 'listed_at', descending: true } as const

const APP_BASE = 'https://app.autograb.com.au/sourcing'

/**
 * The exact AutoGrab view the team works from.
 *
 * Built rather than pasted so the tag list has one home: change
 * EXCLUDED_TAG_IDS and the deep link, the API request and the local filter all
 * move together.
 */
export function sourcingViewUrl(extra?: Record<string, string>): string {
  const params = new URLSearchParams({
    sort_by: SOURCING_SORT.sortBy,
    sort_descending: String(SOURCING_SORT.descending),
    product: 'sourcing',
    excluded_tag_ids: EXCLUDED_TAG_IDS.join(','),
    listing_categories: LISTING_CATEGORIES.join(','),
    ...(extra || {}),
  })
  return `${APP_BASE}?${params.toString()}`
}

/** A listing as the feed hands it to us. Deliberately loose — this is theirs. */
export interface SourcingListing {
  id?: string | number
  vehicle_id?: string | number
  year?: number
  make?: string
  model?: string
  badge?: string
  odometer?: number | null
  price?: number | null
  listed_at?: string | null
  seller_name?: string
  seller_type?: string
  listing_category?: string
  tags?: string[]
  tag_ids?: string[]
  state?: string
  photos?: string[]
  [k: string]: unknown
}

const tagsOf = (l: SourcingListing): string[] => {
  const raw = [...(l.tag_ids || []), ...(l.tags || [])]
  return raw.filter(Boolean).map((t) => String(t).toLowerCase())
}

/**
 * Would Direct Auto look at this listing?
 *
 * A tag matches if it is the excluded tag itself or sits underneath it —
 * `damaged` excludes `damaged/hail` — because AutoGrab's tags are a hierarchy
 * and the saved view lists both the parent and several children.
 */
export function isSourceable(listing: SourcingListing): boolean {
  const tags = tagsOf(listing)
  const blocked = tags.some((t) =>
    EXCLUDED_TAG_IDS.some((ex) => t === ex || t.startsWith(`${ex}/`))
  )
  if (blocked) return false

  // No odometer is its own exclusion in the saved view, and a listing can carry
  // that fact as a null rather than a tag.
  if (listing.odometer === null || listing.odometer === undefined) {
    if (tags.includes('missing_odometer')) return false
  }

  const category = String(listing.listing_category || '').toLowerCase()
  if (category && !LISTING_CATEGORIES.includes(category as 'retail')) return false

  return true
}

export function excludedReason(listing: SourcingListing): string | null {
  const tags = tagsOf(listing)
  const hit = tags.find((t) => EXCLUDED_TAG_IDS.some((ex) => t === ex || t.startsWith(`${ex}/`)))
  if (hit) return `Excluded by the "${hit}" tag.`
  const category = String(listing.listing_category || '').toLowerCase()
  if (category && !LISTING_CATEGORIES.includes(category as 'retail')) {
    return `Excluded — ${category} listing, and the sourcing view is retail only.`
  }
  return null
}

/** Newest listing first, matching sort_by=listed_at&sort_descending=true. */
export function sortByListedAt(listings: SourcingListing[]): SourcingListing[] {
  return [...listings].sort(
    (a, b) => new Date(b.listed_at || 0).getTime() - new Date(a.listed_at || 0).getTime()
  )
}

/** Apply the saved view's rules to a batch of listings. */
export function applySourcingPolicy(listings: SourcingListing[]): {
  kept: SourcingListing[]
  excluded: { listing: SourcingListing; reason: string }[]
} {
  const kept: SourcingListing[] = []
  const excluded: { listing: SourcingListing; reason: string }[] = []
  for (const l of listings) {
    if (isSourceable(l)) kept.push(l)
    else excluded.push({ listing: l, reason: excludedReason(l) || 'Excluded by the sourcing filters.' })
  }
  return { kept: sortByListedAt(kept), excluded }
}

/* ------------------------------------------------------------------ *
 * Pulling the feed
 * ------------------------------------------------------------------ */

export interface FeedResult {
  ok: boolean
  message: string
  listings: SourcingListing[]
  excludedCount: number
}

/**
 * Fetch the sourcing feed through our own endpoint.
 *
 * The endpoint returns 501 until AutoGrab enables a listings feed on the
 * account and it is configured, so this reports that plainly rather than
 * returning an empty list that reads like "nothing for sale today".
 */
export async function fetchSourcingFeed(limit = 100): Promise<FeedResult> {
  let res: Response
  try {
    res = await fetch(`/api/autograb/sourcing-feed?limit=${limit}`)
  } catch (e) {
    return {
      ok: false,
      listings: [],
      excludedCount: 0,
      message: e instanceof Error ? e.message : 'Could not reach the sourcing feed.',
    }
  }

  if (!(res.headers.get('content-type') || '').includes('application/json')) {
    return { ok: false, listings: [], excludedCount: 0, message: 'The sourcing feed is not deployed on this environment.' }
  }

  const body = await res.json().catch(() => ({}) as any)
  if (!res.ok || !body?.success) {
    return {
      ok: false,
      listings: [],
      excludedCount: 0,
      message: body?.error || `The sourcing feed returned ${res.status}.`,
    }
  }

  const { kept, excluded } = applySourcingPolicy(body.listings || [])
  return {
    ok: true,
    listings: kept,
    excludedCount: excluded.length,
    message: `${kept.length} listing${kept.length === 1 ? '' : 's'}${
      excluded.length ? `, ${excluded.length} filtered out` : ''
    }.`,
  }
}
